import { useCancelImportExportHistory } from '@/context/services/import_export_histories/ImportExportHistories.service';
import type { OnCancelClick } from '@/features/import_export_histories/type';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback } from 'react';

export type UseCancelImportExportHistoryClickResult = {
  onCancelClick: OnCancelClick;
  isCancelLoading: boolean;
};

export const useCancelImportExportHistoryClick =
  (): UseCancelImportExportHistoryClickResult => {
    const { t } = useTranslation();
    const notify = useToastAPI();
    const { send } = useErrorTrackingSystem();
    const { mutateAsync, isPending } = useCancelImportExportHistory();

    const onCancelClick = useCallback<OnCancelClick>(
      async (fileId: string) => {
        try {
          const result = await mutateAsync(fileId);
          if (result.status === 'success') {
            notify.success({
              title: t('import_export_history_cancel_success'),
              duration: 5,
            });
          } else {
            const messages = result.errors?.messages ?? [];
            const title =
              messages.length > 0 ? messages?.join('\n') : t('system_error');
            notify.error({ title, duration: 5 });
          }
        } catch (error: unknown) {
          if (error instanceof Error) {
            send(error);
          } else {
            send(new Error(String(error)));
          }
          notify.error({
            title: t('system_error'),
            duration: 5,
          });
        }
      },
      [mutateAsync, notify, send, t]
    );

    return { onCancelClick, isCancelLoading: isPending };
  };
